<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'coursePrivateCoachingModuleList' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('course.private_coaching.title') }}
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header">
          {{ $t('course.private_coaching.module.title') }}
        </h5>

        <div v-if="isLoaded">
          <div class="row">
            <div class="col-lg-8 col-md-12 col-sm-12">
              <form @submit.prevent="submitForm" novalidate>
                <div class="row">
                  <div class="col-lg-12 col-md-12 col-sm-12">
                    <BaseInput v-model="formData.name"
                               :label="$t('general.name')"
                               :required=true
                               :validation="v$.name"
                               :messages="v$.name.required.$message"/>
                  </div>
                  <div class="col-lg-2 col-md-4 col-sm-12">
                    <BaseNumber v-model="formData.cost"
                                :label="$t('general.cost')"
                                :required=true
                                :isCurrency=true
                                :decimalPlaces="4"
                                :validation="v$.cost"
                                :messages="v$.cost.required.$message"/>
                  </div>
                  <div class="col-lg-2 col-md-4 col-sm-12">
                    <BaseInput v-model="formData.units"
                               :label="$t('general.units')"
                               type="number"
                               :required=true
                               :validation="v$.units"
                               :messages="v$.units.required.$message"/>
                  </div>
                  <div class="col-lg-2 col-md-4 col-sm-12">
                    <BaseInput v-model="formData.orderNumber"
                               :label="$t('general.order_number')"
                               type="number"
                               :required=true
                               :validation="v$.orderNumber"
                               :messages="v$.orderNumber.required.$message"
                               readonly/>
                  </div>
                  <div class="col-lg-6 col-md-12 col-sm-12">
                    <SelectVue
                        v-model="formData.operationType"
                        :options="operationTypeFiltered"
                        :label="$t('general.operation_type')"
                        :required=true
                        :validation="v$.operationType"
                        :messages="v$.operationType.required.$message"/>
                  </div>
                </div>
                <div class="row mt-2" v-if="countGoals === 0">
                  <div class="col-12">
                    <SelectVue
                        v-model="formData.copyModuleId"
                        :options="existedModulesDropdown"
                        label="Kopieren Sie Ziele aus dem Modul"
                        placeholder="Modul auswählen"/>
                  </div>
                </div>
                <div class="form-buttons-w text-center">
                  <SaveButton
                      :label="$t('general.button.save')"
                      :loading="isSubmitting"/>
                </div>
              </form>
            </div>
          </div>

          <div class="row text-end mt-4" v-if="moduleId">
            <div class="col-12">
              <button type="button" class="btn btn-primary" @click="handleCreateGoalModalForm">
                Hauptziel erstellen
              </button>
            </div>
          </div>

          <div class="row">
            <div v-if="goals.length > 0 && moduleId" class="col-12 mt-4">
              <VueDraggableNext v-model="goals" :group="'goals'" :animation="200" @end="onDragEnd">
                <GoalItem v-for="goal in goals" :key="goal.id" :goal="goal"
                          v-model="showEventModal"
                          v-model:showDeleteModal="showDeleteModal"
                          v-model:goalId="goalId"
                          v-model:parentGoalId="parentGoalId"/>
              </VueDraggableNext>
            </div>
          </div>
        </div>
      </div>

      <Modal
          v-model="showEventModal"
          :title="t('course.private_coaching.module.create_goal')"
          :hide-cancel="true"
          :button-label="$t('general.button.save') "
          :on-save="() => submitGoalForm()"
          classFooter="justify-content-center">

        <form @submit.prevent="submitGoalForm" novalidate>
          <div class="row">
            <div class="col-12">
              <BaseInput v-model="formGoalData.shortName"
                         :label="$t('general.short_name')"
                         :required=true
                         :validation="vGoals$.shortName"
                         :messages="vGoals$.shortName.required.$message">
              </BaseInput>
            </div>
            <div class="col-12">
              <BaseTextArea v-model="formGoalData.name"
                            :label="$t('general.name')"
                            :required=true
                            :validation="vGoals$.name"
                            :messages="vGoals$.name.required.$message">
              </BaseTextArea>
            </div>
          </div>
        </form>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteModuleGoal">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>
import coursePrivateCoachingModuleService from '@/api/Course/PrivateCoachingModule';
import dropdownService from '@/api/Dropdown';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import BaseTextArea from '@/components/base/BaseTextArea.vue';
import CreateButton from '@/components/CreateButton.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import SelectVue from '@/components/SelectVue.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { requiredRule } from '@/shared/validators/validationRules';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import GoalItem from '@/views/App/Course/PrivateCoachingModule/GoalItem.vue';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, ref, watch } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const { t }                  = useI18n();
const isSubmitting           = ref(false);
const route                  = useRoute();
const moduleId               = route.params.id;
const goalId                 = ref(null);
const parentGoalId           = ref(null);
const goals                  = ref([]);
const isLoaded               = ref(false);
const showEventModal         = ref(false);
const showDeleteModal        = ref(false);
const existedModulesDropdown = ref([]);
const countGoals             = ref(0);

const piniaEnumsState       = piniaEnumsStore();
const operationTypeFiltered = computed(() => {
  return piniaEnumsState.enums.privateCoachingOperationTypes.filter(status =>
      status.value === 'modular_integration_coaching' || status.value === 'go_work_mobile',
  );
});

const formGoalData = ref({
  moduleId: null,
  parentGoalId: null,
  shortName: '',
  name: '',
});

const goalRules = ref({
  shortName: requiredRule(t),
  name: requiredRule(t),
});

const computedRulesGoals = computed(() => goalRules.value);
const vGoals$            = useVuelidate(computedRulesGoals, formGoalData);

const formData = ref({
  name: '',
  cost: '',
  units: '',
  orderNumber: '',
  operationType: 'modular_integration_coaching',
  copyModuleId: '',
});

const rules = ref({
  name: requiredRule(t),
  cost: requiredRule(t),
  units: requiredRule(t),
  orderNumber: requiredRule(t),
  operationType: requiredRule(t),
});

const computedRules = computed(() => rules.value);
const v$            = useVuelidate(computedRules, formData);

watch(showEventModal, (newValue) => {
  CloseToast();

  if (!newValue) {
    formGoalData.value = {};
    goalId.value       = null;
  }

  if (goalId.value) {
    getSingleGoal();
  }
});

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (moduleId) {
      await coursePrivateCoachingModuleService.putModule(formData.value, +moduleId);
    } else {
      await coursePrivateCoachingModuleService.postModule(formData.value);
    }

    isSubmitting.value = false;

    await router.push({ name: 'coursePrivateCoachingModuleList' });
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function submitGoalForm() {
  vGoals$.value.$touch();
  if (vGoals$.value.$invalid) {
    return;
  }

  try {
    formGoalData.value.moduleId     = +moduleId;
    formGoalData.value.parentGoalId = +parentGoalId.value;

    if (goalId.value) {
      await coursePrivateCoachingModuleService.putModuleGoal(formGoalData.value, +goalId.value);
    } else {
      await coursePrivateCoachingModuleService.postModuleGoal(formGoalData.value);
    }

    isSubmitting.value   = false;
    showEventModal.value = false;
    await getModuleGoals();

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getModule() {
  try {
    const { data } = await coursePrivateCoachingModuleService.getModule(+moduleId);
    formData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getNextOrderNumber() {
  try {
    const { data }             = await coursePrivateCoachingModuleService.getNextOrderNumber();
    formData.value.orderNumber = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getModuleGoals() {
  try {
    const { data } = await coursePrivateCoachingModuleService.getModuleGoals(+moduleId);
    goals.value    = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getSingleGoal() {
  try {
    const { data }     = await coursePrivateCoachingModuleService.getModuleGoal(+goalId.value);
    formGoalData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function deleteModuleGoal() {
  try {
    showDeleteModal.value = false;

    await coursePrivateCoachingModuleService.deleteModuleGoal(+goalId.value);

    await getModuleGoals();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getModulesDropdown() {
  const { data }               = await dropdownService.getCoursePrivateCoachingModule('modular_integration_coaching');
  existedModulesDropdown.value = data.data.map(item => ({
    label: `${item.name}`,
    value: item?.id,
  }));
}

async function countModuleGoals() {
  if (moduleId) {
    const { data }   = await coursePrivateCoachingModuleService.countGoals(moduleId);
    countGoals.value = data.data;
  }
}

function handleCreateGoalModalForm() {
  showEventModal.value = true;
  goalId.value         = null;
  parentGoalId.value   = null;
}

const onDragEnd = async (event) => {
  const movedGoalId        = event.item.getAttribute('data-id'); // Get the moved element ID
  const parentIdBeforeMove = event.item.getAttribute('data-parent-id'); // Get current parent id of a moved element
  let newParentId          = null; // Get the element before the moved one (i.e., sibling or parent)
  const previousElement    = event.item.previousElementSibling;   // Find the closest goal above the moved element in the DOM structure

  if (previousElement) {
    newParentId = previousElement.getAttribute('data-id');
  }

  try {

    const { data } = await coursePrivateCoachingModuleService.goalChangePosition({
      moduleId: moduleId,
      movedGoalId: movedGoalId,
      parentIdBeforeMove: parentIdBeforeMove,
      newParentId: newParentId,
      goals: goals.value,
    });
    goals.value    = data.data;

    SuccessToast(t('general.toasts.save_success'));
    isSubmitting.value = false;
  } catch (error) {
    await ErrorToast(error);
  }

};

onMounted(async () => {
  if (moduleId) {
    await Promise.all([
      getModule(),
      getModuleGoals(),
      getModulesDropdown(),
      countModuleGoals(),
    ]);
  } else {
    await Promise.all([
      getNextOrderNumber(),
      getModulesDropdown(),
    ]);
  }

  isLoaded.value = true;
});

</script>

<style scoped>
</style>